import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UtilityService } from '../utility.service';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { environment } from 'environments/environment';
import { PurchasingEmployeeView } from 'app/models/purchasing/PurchasingEmployeeView';
import { VendorPaymentValidation } from 'app/models/form/VendorPaymentValidation';

@Injectable({
	providedIn: 'root',
})
export class PurchasingEmployeeService {
	headers = new HttpHeaders({ 'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0' });

	private readonly urlPath = `${environment.apiUrl}purchasing/clients/`;

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	createForm(clientId: string, request: any): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form`, this.setupRequest(request))
			.pipe();
	}

	private setupRequest(request: any) {
		const formData: FormData = new FormData();
		formData.append('model', JSON.stringify(request));
		return formData;
	}

	updateForm(clientId: string, id: string, request: any): Observable<any> {
		return this.httpClient
			.put<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}`, this.setupRequest(request))
			.pipe();
	}
	deleteDocument(clientId: string, formId: any, docId: any): Observable<any> {
		return this.httpClient.delete<any>(
			`${this.urlPath}${clientId}/forms/purchasing-employee-form/${formId}/docs/${docId}`
		);
	}
	getForm(clientId: string, id: string): Observable<any> {
		return this.httpClient.get<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}`).pipe();
	}

	getAllForms(clientId: string, request: any): Observable<any[]> {
		return this.httpClient
			.post<any[]>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${request}/all`, {})
			.pipe();
	}
	rejectForm(clientId: string, id: string, rejectionReason: { reason: string; workflowId: number }): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, id: string, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/approve`, {
				userId,
			})
			.pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/forms/purchasing-employee-form/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	sendBack(clientId: string, id: string): Observable<any> {
		return this.httpClient
			.post<any>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/review`, {})
			.pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient.delete(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}`).pipe();
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<PurchasingEmployeeView> {
		return this.httpClient
			.post<PurchasingEmployeeView>(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	exportFormPayments(clientId: string, formId: string): Observable<any> {
		return this.httpClient
			.get(`${this.urlPath}${clientId}/forms/purchasing-employee-form/${formId}/export-purchasing-employee-form`, {
				responseType: 'blob',
			})
			.pipe();
	}
	importPurchasingEmployeeForm(clientId: string, request: any): Observable<any> {
		return this.httpClient.post(`${this.urlPath}${clientId}/forms/purchasing-employee-form/import-employee`, request, {
			headers: this.headers,
			responseType: 'text',
		});
	}
	validatePayment(clientId: string, id: string, isAutopay: boolean): Observable<VendorPaymentValidation> {
		return this.httpClient
			.get<VendorPaymentValidation>(
				`${this.urlPath}${clientId}/forms/purchasing-employee-form/${id}/validate-payment/${isAutopay}`
			)
			.pipe();
	}
}
